import { motion, AnimatePresence } from 'framer-motion';
import { ClockIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { formatRemainingTime } from '@/hooks/useActivityTracker';

interface InactivityWarningDialogProps {
  isOpen: boolean;
  remainingTime: number;
  onExtendSession: () => void;
  onLogout: () => void;
}

/**
 * Warning dialog shown when user is about to be logged out due to inactivity
 *
 * Displays:
 * - Countdown timer
 * - "Stay Logged In" button to extend session
 * - "Log Out Now" button for immediate logout
 */
export default function InactivityWarningDialog({
  isOpen,
  remainingTime,
  onExtendSession,
  onLogout,
}: InactivityWarningDialogProps) {
  const formattedTime = formatRemainingTime(remainingTime);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
          />

          {/* Dialog */}
          <div className="fixed inset-0 flex items-start justify-center z-[100] p-4 pt-16 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-md w-full pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="inactivity-dialog-title"
              aria-describedby="inactivity-dialog-description"
            >
              {/* Header */}
              <div className="flex items-start p-6 pb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-warning-100 dark:bg-warning-900/20">
                  <ClockIcon className="w-6 h-6 text-warning-600 dark:text-warning-400" />
                </div>
                <div className="ml-4 flex-1">
                  <h3
                    id="inactivity-dialog-title"
                    className="text-lg font-semibold text-secondary-900 dark:text-white"
                  >
                    Session About to Expire
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 pb-4">
                <p
                  id="inactivity-dialog-description"
                  className="text-secondary-600 dark:text-secondary-400 text-sm leading-relaxed"
                >
                  Your session will expire due to inactivity. You will be logged out in:
                </p>

                {/* Countdown Timer */}
                <div className="mt-4 flex justify-center">
                  <div className="bg-warning-50 dark:bg-warning-900/30 border border-warning-200 dark:border-warning-800 rounded-lg px-6 py-3">
                    <span
                      className="text-3xl font-mono font-bold text-warning-700 dark:text-warning-400"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {formattedTime}
                    </span>
                  </div>
                </div>

                <p className="mt-4 text-secondary-500 dark:text-secondary-500 text-xs text-center">
                  Click &quot;Stay Logged In&quot; to continue your session
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 bg-secondary-50 dark:bg-secondary-800/50 rounded-b-lg">
                <Button
                  variant="outline"
                  onClick={onLogout}
                  className="flex items-center gap-2"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  Log Out Now
                </Button>
                <Button
                  variant="default"
                  onClick={onExtendSession}
                  autoFocus
                >
                  Stay Logged In
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
